import { updateBoxV2 } from "@/api/fulfillment";
import Button from "@/components/forms/Button/Button";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@/components/Modal/Modal";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { FbaBox, useShipmentContext } from "@/contexts/ShipmentContext";
import { useEffect, useState } from "react";
import { DimensionsAndWeight } from "../utils";
import DimensionsAndWeightRow, { boxToDimensionsAndWeight } from "../DimensionsAndWeightRow/DimensionsAndWeightRow";
import Flex from "@/components/Flex/Flex";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import { LocalStorageKeys, setLocalStorageItem } from "@/utils/localStorageUtils";

export interface FbaUpdateBoxDimensionsModalProps {
  box?: FbaBox;
  onClose: () => void;
}

export default function FbaUpdateBoxDimensionsModal(props: FbaUpdateBoxDimensionsModalProps) {
  const { currentBatch, setCurrentBatch } = useShipmentContext();
  const { handleErrorResponseGenerically } = useErrorModalContext();
  const { box, onClose } = props;

  const [loading, setLoading] = useState<boolean>(false);
  const [desiredDimensionsAndWeight, setDesiredDimensionsAndWeight] = useState<DimensionsAndWeight>(boxToDimensionsAndWeight(box));

  useEffect(() => {
    if (!!box) {
      const dimensions = boxToDimensionsAndWeight(box);
      if (dimensions.height && dimensions.width && dimensions.length && dimensions.weight) setDesiredDimensionsAndWeight({...dimensions});
    }
  }, [box]);

  return (
    <Modal
      open={!!box}
      onOpenChange={(open: boolean) => {
        if (!open) onClose();
      }}
    >
      <ModalHeader><h3>Update weight and dimensions</h3></ModalHeader>

      <ModalBody>
        <p className="weak">
          Courier rates can depend on the boxes' dimensions and weight.
        </p>
        <Flex gap={4} style={{margin: "1rem"}}>
          <DimensionsAndWeightRow
            oversizedControls
            showMaxWeightAndDimensionsTooltip
            value={desiredDimensionsAndWeight}
            onChange={(e) => setDesiredDimensionsAndWeight(e)}
          />
        </Flex>
      </ModalBody>

      <ModalFooter>
        <Flex
          justify="end"
          gap={2}
        >
          {loading 
            ? <LoadingSpinner /> 
            : <>
              <Button
                size="large"
                type="secondary"
                onClick={() => onClose()}
              >
                Cancel
              </Button>
              <Button
                size="large"
                type="primary"
                onClick={() => {
                  setLoading(true);
                  const dto = {
                    items: box.packingItems, 
                    length: desiredDimensionsAndWeight.length.toString(),
                    width: desiredDimensionsAndWeight.width.toString(),
                    height: desiredDimensionsAndWeight.height.toString(),
                    weight: desiredDimensionsAndWeight.weight.toString(),
                    packingGroupId: box.packingGroupId,
                  };
                  updateBoxV2(box.id, dto)
                    .then(_ => {
                      Object.assign(box, dto);
                      setLocalStorageItem(LocalStorageKeys.lastUsedDimensionsAndWeight, desiredDimensionsAndWeight);
                      setCurrentBatch({ ...currentBatch });   
                    })
                    .catch(errors => {
                      handleErrorResponseGenerically(errors);
                    })
                    .finally(() => {
                      setLoading(false);
                      onClose?.();
                    });
                }}
              >
                Update
              </Button>
            </>
          }
        </Flex>
      </ModalFooter>
    </Modal>
  );
}
